﻿import { useAuthenticationStore } from '@/stores/useAuthenticationStore'
import { useAdminStore } from '@/stores/useAdminStore'
import { useLoadingStore } from '@/stores/useLoadingStore'
import { useUserStore } from '@/stores/userStore'
import { useVersionStore } from '@/stores/versionStore'
import { useErrorStore } from '@/stores/errorStore'
import { loginRequest } from '@/utils/msal'
import { storeToRefs } from 'pinia'
import type { RouteLocationNormalized, Router } from 'vue-router'
import { CustomNavigationClient } from './navigationClient'

export function registerGuard(router: Router) {
    /**
     * CONSOLIDATED ROUTER GUARD
     * Handles version checks (test mode), IP protection, and authentication
     */
    router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
        // Safety net: programmatic router navigations to event detail should full-load Nuxt SSR.
        // Prefer <a href={getEventPagePath(id)}> in UI to avoid NAVIGATION_ABORTED rejections.
        if (import.meta.env.SSR !== true && to.name === 'event-page') {
            window.location.assign(to.fullPath)
            return false
        }

        // 1. Loading state start
        const loadingStore = useLoadingStore()
        loadingStore.startLoading()

        // 2. Version Check (Support testing via query param: ?testVersionUpdate=old)
        // Background version checking is handled by useVersionPolling/useVersionCheckUpdate
        const versionStore = useVersionStore()
        if (to.query.testVersionUpdate === 'old') {
            versionStore.setUpdateAvailable(true, 'test-version')
        }

        // 4. Authentication Check
        if (to.meta?.requiresAuth) {
            const authStore = useAuthenticationStore()
            // Ensure auth is initialized with the custom navigation client
            await authStore.initialize(new CustomNavigationClient(router))
            const { account } = storeToRefs(authStore)

            if (account.value) {
                // Already logged in
                return true
            }

            // Not logged in but requires auth - trigger login
            const request = {
                ...loginRequest,
                redirectStartPage: to.fullPath
            }
            
            try {
                await authStore.login(request)
                return true
            } catch (err) {
                console.error('Auth login failed:', err)
                loadingStore.stopLoading()
                return { path: '/failed' }
            }
        }

        // 5. Admin role and IP protection
        if (to.meta.isAdmin) {
            const adminStore = useAdminStore()
            const userStore = useUserStore()

            const userState = await userStore.loadUser()
            adminStore.setCurrentRoles(userState.user.roles ?? [])

            if (!adminStore.hasAdminAccess) {
                loadingStore.stopLoading()
                return { name: 'admin-access-denied' }
            }

            const isValid = await adminStore.validateIp()
            if (!isValid) {
                console.warn('🚫 Access denied for IP:', adminStore.lastDetectedIp)
                loadingStore.stopLoading()
                return { name: 'ip-not-allowed' }
            }
        }

        return true // Allow navigation for all other cases
    })

    router.afterEach(() => {
        const loadingStore = useLoadingStore()
        loadingStore.stopLoading()
    })
}

